import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Container, Paper, Grid, Chip, Avatar, Divider,
  Button, IconButton, CircularProgress, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, FormControl, InputLabel, Select,
  MenuItem, Alert, FormControlLabel, Switch,
} from '@mui/material';
import {
  Edit as EditIcon, Delete as DeleteIcon, Timer as TimerIcon,
  Save as SaveIcon, Cancel as CancelIcon, PlayArrow as PlayIcon,
  Stop as StopIcon, Add as AddIcon, ArrowBack as BackIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { taskService } from '../services/taskService';
import { taskStatusService, TaskStatus } from '../services/taskStatusService';
import { timeTrackingService } from '../services/timeTrackingService';
import CommentsSection from '../components/CommentsSection';
import api from '../services/api';

const getPriorityLabel = (p: string | number) => {
  const map: Record<string, string> = { '1': 'Low', '2': 'Medium', '3': 'High', '4': 'Urgent' };
  return map[String(p)] || String(p);
};

const getPriorityColor = (p: string | number): any => {
  switch (String(p)) {
    case '4': return 'error';
    case '3': return 'warning';
    case '2': return 'info';
    case '1': return 'success';
    default: return 'default';
  }
};

const formatMinutes = (mins: number | null | undefined) => {
  if (!mins) return '-';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const TaskDetailsPage: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();

  const [task, setTask] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statuses, setStatuses] = useState<TaskStatus[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [timeLogs, setTimeLogs] = useState<any[]>([]);
  const [activeTimer, setActiveTimer] = useState<any>(null);

  // Editing state
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ title: '', description: '' });

  // Subtask dialog
  const [subtaskDialogOpen, setSubtaskDialogOpen] = useState(false);
  const [subtaskTitle, setSubtaskTitle] = useState('');
  const [subtaskStartDate, setSubtaskStartDate] = useState('');
  const [subtaskDueDate, setSubtaskDueDate] = useState('');

  // Dependencies
  const [dependencies, setDependencies] = useState<any[]>([]);
  const [dependencyDialogOpen, setDependencyDialogOpen] = useState(false);
  const [allTasks, setAllTasks] = useState<any[]>([]);
  const [selectedDependencyTask, setSelectedDependencyTask] = useState('');
  const [dependencyType, setDependencyType] = useState('blocks');

  // Time log dialog
  const [timeLogDialog, setTimeLogDialog] = useState(false);
  const [timeLogForm, setTimeLogForm] = useState({
    description: '', duration: 60, loggedAt: format(new Date(), 'yyyy-MM-dd'),
    workType: 'Development', isBillable: true,
  });

  const loadTask = useCallback(async () => {
    if (!taskId) return;
    try {
      setLoading(true);
      const [taskData, statusesData, logsData, usersResp] = await Promise.all([
        taskService.getTask(taskId),
        taskStatusService.getTaskStatuses(),
        taskService.getTaskTimeLogs(taskId).catch(() => []),
        api.get('/users').catch(() => ({ data: [] })),
      ]);
      setTask(taskData);
      setStatuses(statusesData);
      setTimeLogs(logsData);
      setUsers(Array.isArray(usersResp.data) ? usersResp.data : []);
      setEditForm({ title: (taskData as any).title, description: (taskData as any).description || '' });

      // Load dependencies
      try {
        const depsResp = await api.get(`/tasks/${taskId}/dependencies`);
        setDependencies(depsResp.data);
      } catch { setDependencies([]); }

      // Check active timer
      try {
        const timer = await timeTrackingService.getActiveTimer();
        setActiveTimer(timer);
      } catch { setActiveTimer(null); }
    } catch (err) {
      setError('Failed to load task');
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => { loadTask(); }, [loadTask]);

  const handleSaveEdit = async () => {
    if (!taskId) return;
    try {
      await taskService.updateTask(taskId, {
        title: editForm.title,
        description: editForm.description,
      });
      setEditing(false);
      loadTask();
    } catch { setError('Failed to save changes'); }
  };

  const handleStatusChange = async (newStatusId: string) => {
    if (!taskId) return;
    try {
      await taskService.updateTask(taskId, { statusId: newStatusId });
      loadTask();
    } catch { setError('Failed to update status'); }
  };

  const handlePriorityChange = async (newPriority: number) => {
    if (!taskId) return;
    try {
      await taskService.updateTask(taskId, { priority: newPriority });
      loadTask();
    } catch { setError('Failed to update priority'); }
  };

  const handleAssigneeChange = async (userId: string) => {
    if (!taskId) return;
    try {
      await taskService.updateTask(taskId, { assigneeId: userId || undefined } as any);
      loadTask();
    } catch { setError('Failed to update assignee'); }
  };

  const handleStartTimer = async () => {
    if (!taskId) return;
    try {
      const timer = await timeTrackingService.startTimer({ taskId });
      setActiveTimer(timer);
      setError(null);
    } catch (err: any) {
      const msg = err.response?.data?.message || err.response?.data || err.message || 'Unknown error';
      setError('Failed to start timer: ' + msg);
      console.error('Timer start error:', err);
    }
  };

  const handleStopTimer = async () => {
    try {
      await timeTrackingService.stopTimer();
      setActiveTimer(null);
      setError(null);
      loadTask();
    } catch (err: any) {
      const msg = err.response?.data?.message || err.response?.data || err.message || 'Unknown error';
      setError('Failed to stop timer: ' + msg);
      console.error('Timer stop error:', err);
    }
  };

  const handleAddTimeLog = async () => {
    if (!taskId) return;
    try {
      await taskService.addTimeLog(taskId, {
        description: timeLogForm.description || undefined,
        duration: timeLogForm.duration,
        loggedAt: new Date(timeLogForm.loggedAt),
        workType: timeLogForm.workType,
        isBillable: timeLogForm.isBillable,
      });
      setTimeLogDialog(false);
      loadTask();
    } catch { setError('Failed to log time'); }
  };

  const handleDelete = async () => {
    if (!taskId || !window.confirm('Delete this task? This cannot be undone.')) return;
    try {
      await taskService.deleteTask(taskId);
      navigate(-1);
    } catch { setError('Failed to delete task'); }
  };

  const handleCreateSubtask = async () => {
    if (!subtaskTitle.trim() || !task) return;
    try {
      const typesResp = await api.get('/tasktypes');
      const defaultType = typesResp.data.find((t: any) => t.isDefault)?.id || typesResp.data[0]?.id;

      await taskService.createTask({
        projectId: task.projectId,
        title: subtaskTitle,
        priority: task.priority || 2,
        taskTypeId: defaultType,
        parentTaskId: task.id,
        startDate: subtaskStartDate || undefined,
        dueDate: subtaskDueDate || undefined,
      });
      setSubtaskTitle('');
      setSubtaskStartDate('');
      setSubtaskDueDate('');
      setSubtaskDialogOpen(false);
      loadTask();
    } catch (err: any) {
      console.error('Failed to create subtask:', err);
      alert('Failed to create subtask: ' + (err.response?.data?.message || err.message || 'Unknown error'));
    }
  };

  // Dependencies handlers
  const loadDependencies = async () => {
    if (!taskId) return;
    try {
      const resp = await api.get(`/tasks/${taskId}/dependencies`);
      setDependencies(resp.data);
    } catch { setDependencies([]); }
  };

  const handleAddDependency = async () => {
    if (!taskId || !selectedDependencyTask) return;
    try {
      await api.post(`/tasks/${taskId}/dependencies`, {
        dependsOnTaskId: selectedDependencyTask,
        type: dependencyType,
      });
      setDependencyDialogOpen(false);
      setSelectedDependencyTask('');
      setDependencyType('blocks');
      loadDependencies();
    } catch (err: any) {
      alert('Failed to add dependency: ' + (err.response?.data || err.message));
    }
  };

  const handleRemoveDependency = async (depId: string) => {
    if (!taskId) return;
    try {
      await api.delete(`/tasks/${taskId}/dependencies/${depId}`);
      loadDependencies();
    } catch { alert('Failed to remove dependency'); }
  };

  const handleDateChange = async (field: 'startDate' | 'dueDate', value: string) => {
    if (!taskId) return;
    try {
      await taskService.updateTask(taskId, { [field]: value ? new Date(value) : undefined } as any);
      loadTask();
    } catch { setError('Failed to update date'); }
  };

  if (loading) {
    return <Container sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Container>;
  }

  if (!task) {
    return <Container sx={{ mt: 4 }}><Alert severity="error">Task not found</Alert></Container>;
  }

  const totalTimeLogged = timeLogs.reduce((sum: number, l: any) => sum + (l.duration || 0), 0);
  const isTimerOnThisTask = activeTimer && activeTimer.taskId === taskId;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Back button */}
      <Button startIcon={<BackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
        Back
      </Button>

      <Paper sx={{ p: 3, mb: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flexGrow: 1 }}>
            {editing ? (
              <TextField fullWidth variant="outlined" value={editForm.title}
                onChange={e => setEditForm({ ...editForm, title: e.target.value })}
                sx={{ mb: 1, '& input': { fontSize: '1.5rem', fontWeight: 'bold' } }} />
            ) : (
              <Typography variant="h4" fontWeight="bold" gutterBottom>{task.title}</Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              {task.projectKey && <Chip label={task.projectKey} size="small" variant="outlined" />}
              {task.taskTypeName && <Chip label={task.taskTypeName} size="small" variant="outlined" />}
              <Chip label={getPriorityLabel(task.priority)} size="small"
                color={getPriorityColor(task.priority)} />
              {task.statusName && (
                <Chip label={task.statusName} size="small"
                  sx={task.statusColor ? { bgcolor: task.statusColor, color: '#fff' } : {}} />
              )}
              {task.labels?.map((l: any) => (
                <Chip key={l.id} label={l.name} size="small"
                  sx={{ bgcolor: l.color || '#999', color: '#fff' }} />
              ))}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {editing ? (
              <>
                <Button startIcon={<SaveIcon />} variant="contained" onClick={handleSaveEdit}>Save</Button>
                <Button startIcon={<CancelIcon />} onClick={() => setEditing(false)}>Cancel</Button>
              </>
            ) : (
              <>
                <IconButton color="primary" onClick={() => setEditing(true)}><EditIcon /></IconButton>
                <IconButton color="error" onClick={handleDelete}><DeleteIcon /></IconButton>
              </>
            )}
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          {/* Left Column */}
          <Grid item xs={12} md={8}>
            {/* Description */}
            <Typography variant="h6" gutterBottom>Description</Typography>
            {editing ? (
              <TextField fullWidth multiline rows={4} value={editForm.description}
                onChange={e => setEditForm({ ...editForm, description: e.target.value })} sx={{ mb: 3 }} />
            ) : (
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3, whiteSpace: 'pre-wrap' }}>
                {task.description || 'No description provided.'}
              </Typography>
            )}

            {/* Time Tracking */}
            <Typography variant="h6" gutterBottom>Time Tracking</Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={4}>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">Estimated</Typography>
                  <Typography variant="h6">{formatMinutes(task.originalEstimate)}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">Logged</Typography>
                  <Typography variant="h6" color="primary">{formatMinutes(totalTimeLogged)}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={4}>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">Remaining</Typography>
                  <Typography variant="h6">{formatMinutes(task.remainingEstimate)}</Typography>
                </Paper>
              </Grid>
            </Grid>
            <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
              {isTimerOnThisTask ? (
                <Button variant="contained" color="error" startIcon={<StopIcon />}
                  onClick={handleStopTimer}>Stop Timer</Button>
              ) : (
                <Button variant="outlined" startIcon={<PlayIcon />}
                  onClick={handleStartTimer}>Start Timer</Button>
              )}
              <Button variant="outlined" startIcon={<AddIcon />}
                onClick={() => setTimeLogDialog(true)}>Log Time</Button>
            </Box>

            {/* Time Logs */}
            {timeLogs.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>Time Logs</Typography>
                {timeLogs.map((log: any) => (
                  <Box key={log.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid #f0f0f0' }}>
                    <Box>
                      <Typography variant="body2">{log.description || log.workType || 'Work'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {log.userName} - {format(new Date(log.loggedAt), 'MMM d')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip label={formatMinutes(log.duration)} size="small" />
                      {log.isBillable && <Chip label="$" size="small" color="success" sx={{ minWidth: 28 }} />}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            {/* Subtasks */}
            <Divider sx={{ my: 3 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">Subtasks ({task.subTasks?.length || 0})</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={() => setSubtaskDialogOpen(true)}>
                Add Subtask
              </Button>
            </Box>
            {/* Parent task link */}
            {task.parentTaskId && (
              <Box sx={{ mb: 1.5 }}>
                <Chip
                  label={`Parent: ${task.parentTask?.title || 'View Parent Task'}`}
                  size="small"
                  variant="outlined"
                  onClick={() => navigate(`/tasks/${task.parentTaskId}`)}
                  sx={{ cursor: 'pointer' }}
                />
              </Box>
            )}
            {task.subTasks && task.subTasks.length > 0 ? (
              <Box>
                {task.subTasks.map((sub: any) => (
                  <Paper
                    key={sub.id}
                    variant="outlined"
                    sx={{
                      p: 1.5, mb: 1, cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                      borderLeft: `3px solid ${sub.statusColor || '#94A3B8'}`,
                    }}
                    onClick={() => navigate(`/tasks/${sub.id}`)}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{sub.title}</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                          {sub.statusName && (
                            <Chip label={sub.statusName} size="small"
                              sx={{ height: 20, fontSize: '0.65rem', bgcolor: sub.statusColor || '#94A3B8', color: '#fff' }} />
                          )}
                          <Chip label={getPriorityLabel(sub.priority)} size="small"
                            color={getPriorityColor(sub.priority)} sx={{ height: 20, fontSize: '0.65rem' }} />
                        </Box>
                      </Box>
                      {sub.assignee && (
                        <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem' }}>
                          {sub.assignee.firstName?.[0]}
                        </Avatar>
                      )}
                    </Box>
                  </Paper>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                No subtasks yet
              </Typography>
            )}

            {/* Dependencies */}
            <Divider sx={{ my: 3 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">Dependencies ({dependencies.length})</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={() => {
                if (allTasks.length === 0) {
                  taskService.getAllTasks().then(t => setAllTasks(t as any[])).catch(() => {});
                }
                setDependencyDialogOpen(true);
              }}>
                Add
              </Button>
            </Box>
            {dependencies.length > 0 ? (
              <Box>
                {dependencies.map((dep: any) => (
                  <Paper key={dep.id} variant="outlined" sx={{ p: 1, mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Chip
                        label={dep.type === 'blocks' ? 'Blocked by' : dep.type === 'relates_to' ? 'Relates to' : dep.type}
                        size="small"
                        color={dep.type === 'blocks' ? 'error' : 'default'}
                        variant="outlined"
                        sx={{ mr: 1 }}
                      />
                      <Typography variant="body2" component="span"
                        sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                        onClick={() => navigate(`/tasks/${dep.dependsOnTaskId}`)}>
                        {dep.dependsOnTaskTitle || dep.dependsOnTaskId}
                      </Typography>
                    </Box>
                    <IconButton size="small" color="error" onClick={() => handleRemoveDependency(dep.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Paper>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                No dependencies
              </Typography>
            )}

            {/* Comments */}
            <Divider sx={{ my: 3 }} />
            {taskId && <CommentsSection />}
          </Grid>

          {/* Right Column - Details sidebar */}
          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Details</Typography>

              {/* Status */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>Status</Typography>
                <FormControl fullWidth size="small">
                  <Select value={task.statusId || ''} onChange={e => handleStatusChange(e.target.value)}>
                    {statuses.map(s => (
                      <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Priority */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>Priority</Typography>
                <FormControl fullWidth size="small">
                  <Select value={String(task.priority)} onChange={e => handlePriorityChange(Number(e.target.value))}>
                    <MenuItem value="1">Low</MenuItem>
                    <MenuItem value="2">Medium</MenuItem>
                    <MenuItem value="3">High</MenuItem>
                    <MenuItem value="4">Urgent</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {/* Assignee */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>Assignee</Typography>
                <FormControl fullWidth size="small">
                  <Select value={String(task.assignee?.id || '')} displayEmpty
                    onChange={e => handleAssigneeChange(e.target.value)}>
                    <MenuItem value="">Unassigned</MenuItem>
                    {users.map((u: any) => (
                      <MenuItem key={String(u.id)} value={String(u.id)}>{u.firstName} {u.lastName}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Reporter */}
              {task.reporter && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>Reporter</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 28, height: 28, fontSize: '0.8rem' }}>{task.reporter.firstName?.[0]}</Avatar>
                    <Typography variant="body2">{task.reporter.firstName} {task.reporter.lastName}</Typography>
                  </Box>
                </Box>
              )}

              {/* Dates - Editable */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>Start Date</Typography>
                <TextField
                  type="date"
                  size="small"
                  fullWidth
                  value={task.startDate ? format(new Date(task.startDate), 'yyyy-MM-dd') : ''}
                  onChange={(e) => handleDateChange('startDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>Due Date</Typography>
                <TextField
                  type="date"
                  size="small"
                  fullWidth
                  value={task.dueDate ? format(new Date(task.dueDate), 'yyyy-MM-dd') : ''}
                  onChange={(e) => handleDateChange('dueDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              {task.completedAt && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">Completed</Typography>
                  <Typography variant="body2" color="success.main">{format(new Date(task.completedAt), 'MMM dd, yyyy')}</Typography>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary">Project</Typography>
                <Typography variant="body2">{task.projectName || 'N/A'}</Typography>
              </Box>
              {task.storyPoints && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">Story Points</Typography>
                  <Typography variant="body2">{task.storyPoints}</Typography>
                </Box>
              )}
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary">Created</Typography>
                <Typography variant="body2">{format(new Date(task.createdAt), 'MMM dd, yyyy HH:mm')}</Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      {/* Log Time Dialog */}
      <Dialog open={timeLogDialog} onClose={() => setTimeLogDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Log Time</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Description" value={timeLogForm.description}
                onChange={e => setTimeLogForm({ ...timeLogForm, description: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Duration (minutes)" type="number" value={timeLogForm.duration}
                onChange={e => setTimeLogForm({ ...timeLogForm, duration: parseInt(e.target.value) || 0 })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Date" type="date" value={timeLogForm.loggedAt}
                InputLabelProps={{ shrink: true }}
                onChange={e => setTimeLogForm({ ...timeLogForm, loggedAt: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Work Type</InputLabel>
                <Select value={timeLogForm.workType} label="Work Type"
                  onChange={e => setTimeLogForm({ ...timeLogForm, workType: e.target.value })}>
                  <MenuItem value="Development">Development</MenuItem>
                  <MenuItem value="Design">Design</MenuItem>
                  <MenuItem value="Testing">Testing</MenuItem>
                  <MenuItem value="Research">Research</MenuItem>
                  <MenuItem value="Meeting">Meeting</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel sx={{ mt: 1 }}
                control={<Switch checked={timeLogForm.isBillable}
                  onChange={e => setTimeLogForm({ ...timeLogForm, isBillable: e.target.checked })} />}
                label="Billable" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTimeLogDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddTimeLog}>Log Time</Button>
        </DialogActions>
      </Dialog>

      {/* Subtask Dialog */}
      <Dialog open={subtaskDialogOpen} onClose={() => setSubtaskDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Subtask</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Subtask Title"
            value={subtaskTitle}
            onChange={(e) => setSubtaskTitle(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="Start Date"
                type="date"
                fullWidth
                value={subtaskStartDate}
                onChange={(e) => setSubtaskStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Due Date"
                type="date"
                fullWidth
                value={subtaskDueDate}
                onChange={(e) => setSubtaskDueDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
          {task?.dueDate && subtaskDueDate && new Date(subtaskDueDate) > new Date(task.dueDate) && (
            <Typography variant="caption" color="error.main" sx={{ mt: 1, display: 'block' }}>
              Subtask due date exceeds parent task due date ({format(new Date(task.dueDate), 'MMM d, yyyy')})
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubtaskDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateSubtask} disabled={!subtaskTitle.trim()}>
            Create Subtask
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dependency Dialog */}
      <Dialog open={dependencyDialogOpen} onClose={() => setDependencyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Dependency</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
            <InputLabel>Dependency Type</InputLabel>
            <Select value={dependencyType} label="Dependency Type" onChange={e => setDependencyType(e.target.value)}>
              <MenuItem value="blocks">Blocked by (must finish first)</MenuItem>
              <MenuItem value="relates_to">Relates to</MenuItem>
              <MenuItem value="duplicates">Duplicates</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Task</InputLabel>
            <Select value={selectedDependencyTask} label="Task" onChange={e => setSelectedDependencyTask(e.target.value)}>
              {allTasks
                .filter((t: any) => t.id !== taskId)
                .map((t: any) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.title} {t.projectKey ? `(${t.projectKey})` : ''}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDependencyDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddDependency} disabled={!selectedDependencyTask}>
            Add Dependency
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TaskDetailsPage;
